import React, { useEffect } from "react";
import styled from "styled-components";
import { Store, CheckCircle, AlertTriangle, XCircle, Clock } from "lucide-react";
import { useWbStoresStore } from "./use-wb-stores-store";
import { WbStoresFilters } from "./wb-stores-filters";
import { WbStoresTable } from "./wb-stores-table";

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 21px;
  font-weight: 700;
  color: #202020;
  margin: 0;
`;

const TotalBadge = styled.span`
  font-size: 12px;
  opacity: 0.75;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: white;
  border: 1px solid #eee;
  border-radius: 10px;
`;

const StatIconBox = styled.div<{ $bg: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${p => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #202020;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #999;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 5px 10px;
  border: 1px solid ${({ $active }) => ($active ? "#cb11ab" : "#e0e0e0")};
  border-radius: 4px;
  background: ${({ $active }) => ($active ? "#cb11ab" : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#333")};
  cursor: pointer;
  font-size: 13px;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const WbStoresPage: React.FC = () => {
  const { total, stats, page, limit, fetchStores, setPage } = useWbStoresStore();

  useEffect(() => {
    fetchStores();
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <PageContainer>
      <PageHeader>
        <Store size={24} color="#202020" />
        <PageTitle>
          WB магазины
          <TotalBadge> ({stats?.total_stores ?? total})</TotalBadge>
        </PageTitle>
      </PageHeader>

      {stats && (
        <StatsGrid>
          <StatCard $color="#cb11ab">
            <StatIconBox $bg="#fce4ec">
              <Store size={20} color="#cb11ab" />
            </StatIconBox>
            <div>
              <StatValue>{stats.total_stores}</StatValue>
              <StatLabel>Всего магазинов</StatLabel>
            </div>
          </StatCard>
          <StatCard $color="#4caf50">
            <StatIconBox $bg="#e8f5e9">
              <CheckCircle size={20} color="#4caf50" />
            </StatIconBox>
            <div>
              <StatValue>{stats.synced_ok}</StatValue>
              <StatLabel>Синхронизировано</StatLabel>
            </div>
          </StatCard>
          <StatCard $color="#ff9800">
            <StatIconBox $bg="#fff3e0">
              <AlertTriangle size={20} color="#ff9800" />
            </StatIconBox>
            <div>
              <StatValue>{stats.synced_with_errors}</StatValue>
              <StatLabel>С ошибками</StatLabel>
            </div>
          </StatCard>
          <StatCard $color="#f44336">
            <StatIconBox $bg="#ffebee">
              <XCircle size={20} color="#f44336" />
            </StatIconBox>
            <div>
              <StatValue>{stats.sync_failed}</StatValue>
              <StatLabel>Провалено</StatLabel>
            </div>
          </StatCard>
          <StatCard $color="#9e9e9e">
            <StatIconBox $bg="#f5f5f5">
              <Clock size={20} color="#9e9e9e" />
            </StatIconBox>
            <div>
              <StatValue>{stats.never_synced}</StatValue>
              <StatLabel>Не синхронизировано</StatLabel>
            </div>
          </StatCard>
        </StatsGrid>
      )}

      <WbStoresFilters />
      <WbStoresTable />

      {totalPages > 1 && (
        <PaginationRow>
          <PageButton disabled={page <= 1} onClick={() => setPage(page - 1)}>
            ← Назад
          </PageButton>
          <span style={{ fontSize: 13, color: "#888" }}>
            {page} / {totalPages}
          </span>
          <PageButton disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Вперёд →
          </PageButton>
        </PaginationRow>
      )}
    </PageContainer>
  );
};
